import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { Alert, CircularProgress } from "@mui/material";
import { getArticleBySlug, getArticles } from "../../utils/articlesApi";
import "./ArticlePage.css";

function ArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getArticleBySlug(slug);
        const mainArticle = data?.article || data;
        setArticle(mainArticle || null);

        const directRelated = Array.isArray(data?.similar_articles)
          ? data.similar_articles
          : [];
        if (directRelated.length > 0) {
          setRelatedArticles(directRelated.slice(0, 3));
          return;
        }

        const all = await getArticles();
        const fallbackRelated = (Array.isArray(all) ? all : [])
          .filter((entry) => entry.slug !== slug)
          .slice(0, 3);
        setRelatedArticles(fallbackRelated);
      } catch (err) {
        console.error("Error fetching article:", err);
        setError(err.message || "Не можеме да ја вчитаме статијата.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  const formattedDate = useMemo(() => {
    if (!article?.created_at) return "";
    const date = new Date(article.created_at);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("mk-MK", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, [article?.created_at]);

  const formattedUpdatedDate = useMemo(() => {
    if (!article?.updated_at) return "";
    const date = new Date(article.updated_at);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("mk-MK", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, [article?.updated_at]);

  const contentSections = useMemo(() => {
    const sections = [
      { key: "intro", html: article?.intro || "" },
      { key: "main", html: article?.main_body || "" },
      { key: "outro", html: article?.outro || "" },
    ].filter((section) => Boolean(String(section.html).trim()));

    if (sections.length > 0) {
      return sections;
    }

    const fallback =
      article?.body ||
      article?.description ||
      article?.content ||
      article?.short_description ||
      "";

    return fallback ? [{ key: "body", html: fallback }] : [];
  }, [article]);

  const galleryImages = useMemo(
    () =>
      Array.isArray(article?.pictures_urls)
        ? article.pictures_urls.filter(Boolean)
        : [],
    [article?.pictures_urls]
  );

  const coverImageUrl =
    article?.cover_picture_url ||
    article?.cover_picture ||
    "https://placehold.co/1200x700/f6f4ef/7f6f65?text=Kazhavda";

  if (loading) {
    return (
      <div className="article-show-shell article-show-loading">
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div className="article-show-shell article-show-loading">
        <Alert severity="error">{error}</Alert>
      </div>
    );
  }

  if (!article) return null;

  return (
    <div className="article-show-shell">
      <article className="article-show-page-v2">
        <div className="article-show-topbar">
          <button className="article-back-button" onClick={() => navigate(-1)}>
            <ArrowBackIosNewIcon fontSize="small" />
          </button>
          <Link to="/articles" className="article-show-all-link">
            Сите статии
          </Link>
        </div>

        <div className="article-show-hero">
          <img
            src={coverImageUrl}
            alt={article.title}
            className="article-show-image"
          />
          <div className="article-show-summary">
            <span className="article-show-eyebrow">Статија</span>
            <h1 className="article-show-title">{article.title}</h1>
            <div className="article-show-meta">
              {formattedDate ? (
                <p className="article-show-date">Објавено: {formattedDate}</p>
              ) : null}
              {formattedUpdatedDate ? (
                <p className="article-show-date">
                  Ажурирано: {formattedUpdatedDate}
                </p>
              ) : null}
            </div>
            {article.short_description ? (
              <p className="article-show-lead">{article.short_description}</p>
            ) : null}
            <div className="article-show-tags">
              {article?.published !== undefined ? (
                <span
                  className={`article-tag ${
                    article.published ? "is-published" : "is-draft"
                  }`}
                >
                  {article.published ? "Published" : "Draft"}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <section className="article-show-content-card">
          {contentSections.map((section) => (
            <div key={section.key} className="article-show-section">
              <div
                className="article-show-body"
                dangerouslySetInnerHTML={{ __html: section.html }}
              />
            </div>
          ))}
        </section>

        {galleryImages.length > 0 && (
          <section className="article-gallery-section">
            <h2>Галерија</h2>
            <div className="article-gallery-grid">
              {galleryImages.map((imageUrl, index) => (
                <img
                  key={`${imageUrl}-${index}`}
                  src={imageUrl}
                  alt={`${article.title} ${index + 1}`}
                />
              ))}
            </div>
          </section>
        )}

        {relatedArticles.length > 0 && (
          <section className="article-related-section">
            <h2>Поврзани статии</h2>
            <div className="article-related-grid">
              {relatedArticles
                .filter((entry) => Boolean(entry?.slug))
                .map((entry) => (
                <Link
                  key={entry.id}
                  to={`/articles/${entry.slug}`}
                  className="article-related-card"
                >
                  <img
                    src={
                      entry.cover_picture_url ||
                      "https://placehold.co/700x420/f6f4ef/7f6f65?text=Kazhavda"
                    }
                    alt={entry.title}
                  />
                  <div>
                    <h3>{entry.title}</h3>
                    <p>Прочитај повеќе</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </div>
  );
}

export default ArticlePage;
